import { useState, useEffect, useRef } from "react";
import { useT } from "./i18n";
import { fsrsR, fsrsReview, cardMemState } from "./fsrs";
import { save, load, todayISO, daysBetween, formatNumber, getTranslatedTopicName } from "./utils";
import { getCurriculum } from "./curriculum";
import { speak, useWakeLock, getSpeakLangCode } from "./tts";
import { createActivityTimer } from "./activityTimer";
import { getTranslationCache, setTranslationCache } from "./firebase";
import { translateLessonWords } from "./api";

export function ProgressRing({ card, size = 36 }) {
  const today = todayISO();
  const t = card.lastReview ? daysBetween(card.lastReview, today) : 0;
  const S = card.stability;
  const R = S ? fsrsR(t, S) : 0;
  const iv = S ? Math.round(S) : 0;

  let fillPct, ringColor, stateName;
  if (!S || iv < 3) {
    fillPct = 0.08; ringColor = '#64748b'; stateName = 'In Acquisition';
  } else if (iv < 21) {
    fillPct = 0.28 + (iv - 3) / 18 * 0.24; ringColor = '#10b981'; stateName = 'Rooted';
  } else if (iv < 90) {
    fillPct = 0.52 + (iv - 21) / 69 * 0.28; ringColor = '#059669'; stateName = 'Fluent';
  } else {
    fillPct = 1.0; ringColor = '#C9952A'; stateName = 'Deeply Retained';
  }

  const r = (size / 2) - 3, cx = size / 2;
  const circ = 2 * Math.PI * r, dash = circ * fillPct;
  const tip = `${stateName}${S ? ` · ${iv}d stability · ${Math.round(R * 100)}% recall` : ''}`;

  return (
    <div className="ring-wrap" title={tip}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="#e2e8f0" strokeWidth={2.5} />
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={ringColor} strokeWidth={2.5}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cx})`}
          style={{ transition: 'stroke-dasharray 0.5s ease' }} />
        <circle cx={cx} cy={cx} r={r * 0.42}
          fill={ringColor} opacity={S ? Math.max(0.18, R * 0.85) : 0.12} />
      </svg>
    </div>
  );
}

const EyeIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const SpeakIcon = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
// Module-level (not component state) so it survives FlashcardView unmounting/remounting as the
// learner navigates away and back within the same browser tab — only a full page reload clears
// it. Without this, every remount wiped the resolved-translations cache and re-triggered a
// Firestore read (and possibly a live Claude translation call) for every topic in the due queue,
// even when the base language hadn't changed since the last visit. Keyed by topicId, same shape
// startDeck below already expects: { lang, byWordId }.
const resolvedTranslationsCache = {};

const VolumeIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 5 6 9H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h3l5 4V5z" fill="currentColor" stroke="none"/>
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
    <path d="M18.36 5.64a9 9 0 0 1 0 12.72"/>
  </svg>
);

export function FlashcardView({ cards, profile, onComplete, onBack, destLang = "Turkish", onUnsavedChange }) {
  useWakeLock();
  const { t, lang, langName, isRTL } = useT();
  const dirArrow = isRTL ? "←" : "→";
  const today = todayISO();
  const rawBaseLanguage = profile?.baseLanguage || "English";
  const baseLang = langName(rawBaseLanguage);
  const targetLang = langName(destLang);
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const speakLang = getSpeakLangCode(langCode);

  const ratingDefs = [
    {r:1, key:'forgotten', cls:'r-forgot'},
    {r:2, key:'struggled', cls:'r-hard'},
    {r:3, key:'recalled',  cls:'r-good'},
    {r:4, key:'effortless',cls:'r-easy'},
  ];
  const fmtIv = iv => iv === 1 ? t("flashcards.tomorrow") : t("flashcards.nDays", { n: iv });

  const [phase, setPhase] = useState('overview');
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [reviewed, setReviewed] = useState([]);
  const [pendingAdvance, setPendingAdvance] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  // D1-only: whether "Show the Word" has been clicked for the *current* card.
  // Session-only (not persisted), and reset to false any time the queue
  // advances to a new card (see startDeck and the pendingAdvance effect
  // below) — but deliberately untouched by flipping, so flip-to-back-and-
  // -back-to-front preserves whatever reveal state the learner already set
  // for this card. Never read by the D2 (isD2) front branch.
  const [d1Revealed, setD1Revealed] = useState(false);
  // Session time tracking (see startDeck/backToOverview/the done-phase save button below).
  // Not component-lifetime like LessonView's timer — a new one is created per deck session,
  // since the overview screen (between sessions) shouldn't count as study time.
  const timerRef = useRef(null);
  useEffect(() => () => timerRef.current?.stop(), []);

  // Cards bake their meaning/example translation in permanently at the lesson they were learned
  // in, under whatever base language was active then (see makeFSRSCard). If the learner has
  // since switched base language in Goals, that baked-in text never self-corrects — this map
  // (topicId -> { lang, byWordId }) lets startDeck re-resolve it live, per topic, against the
  // CURRENT base language before a session starts. Reset per FlashcardView mount, so a base
  // language switch mid-app-session is picked up the next time Flashcards is opened.
  const [wordTranslations, setWordTranslations] = useState(() => resolvedTranslationsCache);
  const [resolvingSession, setResolvingSession] = useState(false);

  const hasAnyD2 = cards.some(c => c.d2);
  const [showD2Modal, setShowD2Modal] = useState(() => hasAnyD2 && !load("d2Explained"));

  const d1Due = cards.filter(c => !c.d1Mastered && c.due <= today);
  const d2Cards = cards.filter(c => c.d2 && !c.d2Mastered);
  const d2Due  = d2Cards.filter(c => c.d2.due <= today);
  const d2HasCards = d2Cards.length > 0;

  // Reports the in-progress reviewed queue (and minutes so far) up to App.js so it can guard
  // navigation away from this screen (bottom nav, header links, etc.) — see onUnsavedChange in
  // AppShell. Fires on every reviewed change, including the reset to [] on startDeck/
  // backToOverview, so the parent's record never goes stale.
  useEffect(() => {
    onUnsavedChange?.(reviewed, timerRef.current?.getActiveMinutes());
  }, [reviewed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Native browser exits (tab close/refresh) can't be intercepted with a custom dialog — the
  // browser shows its own generic prompt — but we can at least trigger it while there's
  // reviewed-but-unsaved progress, same guard condition as the in-app navigation dialog.
  useEffect(() => {
    if (reviewed.length === 0) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [reviewed.length]);

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    if (pendingAdvance === null) return;
    const timer = setTimeout(() => {
      if (pendingAdvance === 'done') setPhase('done');
      else setIdx(pendingAdvance);
      setD1Revealed(false);
      setPendingAdvance(null);
    }, 520);
    return () => clearTimeout(timer);
  }, [pendingAdvance]);

  const startDeck = async (deck) => {
    timerRef.current?.stop();
    timerRef.current = createActivityTimer();
    const pool = deck === 1
      ? d1Due.map(c => ({ ...c, _deck: 1 }))
      : d2Due.map(c => ({ ...c, _deck: 2 }));
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    // Re-resolve translations for any topic in this pool we haven't already resolved for the
    // current base language — same per-topic cache LessonView uses (Firestore-cached, one live
    // Claude call only on a genuine cache miss). Cards missing topicId (pre-dates its addition)
    // are left out here and fall back to whatever's already baked onto them in the render below.
    const neededTopicIds = [...new Set(pool.map(c => c.topicId).filter(Boolean))]
      .filter(tid => wordTranslations[tid]?.lang !== rawBaseLanguage);

    if (neededTopicIds.length > 0) {
      setResolvingSession(true);
      const updates = {};
      await Promise.all(neededTopicIds.map(async (topicId) => {
        const topic = getCurriculum(langCode).find(t => t.topic_id === topicId);
        if (!topic) return;
        try {
          let cachedWords = await getTranslationCache(langCode, topicId, rawBaseLanguage, topic.words);
          if (!cachedWords) {
            cachedWords = await translateLessonWords(topic.words, rawBaseLanguage, topic.topic, destLang);
            setTranslationCache(langCode, topicId, rawBaseLanguage, topic.words, { topicId, lang: rawBaseLanguage, words: cachedWords }).catch(() => {});
          }
          const byWordId = new Map();
          topic.words.forEach((w, i) => byWordId.set(w.word_id, { meaning: cachedWords[i]?.meaning, exTr: cachedWords[i]?.exTr, example: w.example }));
          updates[topicId] = { lang: rawBaseLanguage, byWordId };
        } catch (err) {
          console.error(`Flashcard translation unavailable for ${topicId}_${rawBaseLanguage}, falling back to the card's baked-in text`, err);
        }
      }));
      Object.assign(resolvedTranslationsCache, updates);
      setWordTranslations(prev => ({ ...prev, ...updates }));
      setResolvingSession(false);
    }

    setQueue(pool);
    setIdx(0);
    setFlipped(false);
    setD1Revealed(false);
    setReviewed([]);
    setPhase('session');
  };

  const faqItems = [
    { key: 'whyTwo',   q: t("flashcards.faq.whyTwoQ"),   a: t("flashcards.faq.whyTwoA",   { targetLang, baseLang }) },
    { key: 'whyLater', q: t("flashcards.faq.whyLaterQ"), a: t("flashcards.faq.whyLaterA") },
    { key: 'whyReturn',q: t("flashcards.faq.whyReturnQ"),a: t("flashcards.faq.whyReturnA") },
  ];

  // ── OVERVIEW ──────────────────────────────────────────────────
  if (phase === 'overview') {
    return (
      <div className="fc-view">
        {resolvingSession && (
          <div className="fc-modal-overlay">
            <div className="fc-modal" style={{textAlign:"center"}}>
              <div className="spinner" style={{margin:"0 auto 14px"}} />
              <div className="fc-modal-body" style={{marginBottom:0}}>{t("lesson.translating", { lang: baseLang })}</div>
            </div>
          </div>
        )}
        {showD2Modal && (
          <div className="fc-modal-overlay">
            <div className="fc-modal">
              <div className="fc-modal-title">{t("flashcards.d2ModalTitle")}</div>
              <p className="fc-modal-body">{t("flashcards.d2ModalBody", { targetLang })}</p>
              <button className="primary-btn" onClick={() => { save("d2Explained", true); setShowD2Modal(false); }}>{t("flashcards.d2ModalBtn")}</button>
            </div>
          </div>
        )}
        <div className="lesson-header">
          <button className="back-btn" onClick={onBack}>{t("lesson.backBtn")}</button>
          <div className="lesson-title">{t("flashcards.title")}</div>
        </div>

        {/* Deck 1 */}
        <div
          className={`fc-deck-panel${d1Due.length > 0 ? " fc-deck-panel--clickable" : " fc-deck-panel--disabled"}`}
          onClick={() => d1Due.length > 0 && !resolvingSession && startDeck(1)}
          role={d1Due.length > 0 ? "button" : undefined}
        >
          <div className="fc-deck-icon" style={{color:'#7B1E1E'}}><EyeIcon /></div>
          <div className="fc-deck-big-title" style={{color:'#7B1E1E'}}>{t("flashcards.d1Title")}</div>
          <div className="fc-deck-direction-lbl">{targetLang} {dirArrow} {baseLang}</div>
          <div className="fc-deck-journey-lbl">{t("flashcards.d1Journey")}</div>
          <div className="fc-deck-footer-row">
            <span className="fc-deck-sub">{t("flashcards.d1SubSkill")}</span>
            <span className="fc-deck-badge">{t("flashcards.d1Due", { n: d1Due.length })}</span>
          </div>
        </div>

        {/* Deck 2 */}
        <div
          className={`fc-deck-panel${d2HasCards && d2Due.length > 0 ? " fc-deck-panel--clickable fc-deck-panel--d2" : " fc-deck-panel--disabled"}`}
          onClick={() => d2HasCards && d2Due.length > 0 && !resolvingSession && startDeck(2)}
          role={d2HasCards && d2Due.length > 0 ? "button" : undefined}
        >
          <div className="fc-deck-icon" style={{color: d2HasCards ? '#059669' : '#B0A090'}}><SpeakIcon /></div>
          <div className="fc-deck-big-title" style={{color: d2HasCards ? '#059669' : '#B0A090'}}>{t("flashcards.d2Title")}</div>
          <div className="fc-deck-direction-lbl">{baseLang} {dirArrow} {targetLang}</div>
          {!d2HasCards && <div className="fc-deck-journey-lbl">{t("flashcards.d2Empty")}</div>}
          <div className="fc-deck-footer-row">
            <span className="fc-deck-sub">{t("flashcards.d2SubSkill")}</span>
            {d2HasCards
              ? <span className="fc-deck-badge fc-deck-badge--d2">{t("flashcards.d1Due", { n: d2Due.length })}</span>
              : <span className="fc-deck-badge fc-deck-badge--dim">{t("flashcards.d2WillStart")}</span>
            }
          </div>
        </div>

        {/* FAQ accordion */}
        <div className="fc-faq">
          {faqItems.map(({ key, q, a }) => (
            <div key={key} className="fc-faq-item">
              <button className="fc-faq-q" onClick={() => setOpenFaq(openFaq === key ? null : key)}>
                <span>{q}</span>
                <span className="fc-faq-chevron">{openFaq === key ? '▲' : '▼'}</span>
              </button>
              {openFaq === key && (
                <div className="fc-faq-a">
                  {String(a).split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── SESSION DONE ──────────────────────────────────────────────
  if (phase === 'done') {
    const total = reviewed.length;
    const byRating = [1,2,3,4].map(r => reviewed.filter(c => c._rating === r).length);
    const retained = byRating[1] + byRating[2] + byRating[3];
    const pct = Math.round(retained / total * 100);
    const emoji = pct >= 85 ? "🌟" : pct >= 65 ? "💪" : "📚";
    return (
      <div className="fc-view">
        <div className="lesson-header">
          <div className="lesson-title">{t("flashcards.sessionComplete")}</div>
        </div>
        <div className="fc-session-done">
          <div className="fc-done-emoji">{emoji}</div>
          <div className="fc-done-pct">{formatNumber(pct, lang)}%</div>
          <div className="fc-done-sub">{t("flashcards.memoryRetention")}</div>
          <div className="fc-rating-breakdown">
            {ratingDefs.map(({r,key,cls}) => (
              <div key={r} className={`fc-rating-stat ${cls}`}>
                <div className="fc-rating-stat-n">{formatNumber(byRating[r-1], lang)}</div>
                <div className="fc-rating-stat-l">{t(`flashcards.${key}`)}</div>
              </div>
            ))}
          </div>
          <div className="fc-done-note">
            {pct >= 70
              ? t("flashcards.wordsConsolidated", { retained, total })
              : t("flashcards.wordsInProgress", { retained, total })}
          </div>
          <button className="primary-btn" style={{maxWidth:240,marginTop:8}} onClick={() => {
            const minutes = timerRef.current?.getActiveMinutes();
            timerRef.current?.stop();
            timerRef.current = null;
            onComplete(reviewed, { minutes });
          }}>
            {t("flashcards.saveContinue")}
          </button>
        </div>
      </div>
    );
  }

  // ── SESSION ───────────────────────────────────────────────────
  const card = queue[idx];
  const isD2 = card._deck === 2;
  // Older cards may lack topicId (pre-dates its addition to fsrs.js) — those fall back to
  // the English name already cached on the card, same as before this feature.
  const cardTopic = card.topicId && getCurriculum(langCode).find(t => t.topic_id === card.topicId);
  const topicLabel = cardTopic ? getTranslatedTopicName(cardTopic, lang) : card.topicName;
  // Live-resolved translation for the CURRENT base language (see startDeck) takes priority over
  // whatever the card has baked in from whenever it was originally learned — falls back to the
  // baked-in text only when live resolution wasn't possible (no topicId, topic removed from
  // curriculum, or the resolution call failed).
  const liveTranslation = card.topicId && wordTranslations[card.topicId]?.lang === rawBaseLanguage
    ? wordTranslations[card.topicId].byWordId.get(card.id)
    : null;
  const cardEnglish = liveTranslation?.meaning || card.english;
  // example and exampleTranslation must come from the same source (both live or both baked) —
  // mixing a live-current Turkish sentence with a baked-in translation (or vice versa) would
  // pair mismatched text, since the curriculum's example sentences can change independently
  // of a card's creation-time snapshot.
  const cardExample = liveTranslation?.example || card.example;
  const cardExampleTranslation = liveTranslation?.exTr || card.example_translation;
  const cardState = isD2 ? card.d2 : card;
  const previewIv = [1,2,3,4].map(r => fsrsReview(cardState, r).interval);
  const { key: stateKey, color: stateColor } = cardMemState(cardState);
  const stateLabel = t(`memStates.${stateKey}`);

  const rate = (rating) => {
    let updated;
    if (isD2) {
      updated = { ...card, d2: fsrsReview(card.d2, rating), _rating: rating, _deck: 2 };
    } else {
      updated = { ...fsrsReview(card, rating), _rating: rating, _deck: 1 };
    }
    setReviewed(prev => [...prev, updated]);
    setFlipped(false);
    // Advance only after the flip animation completes (520ms) so the next
    // card's answer is never visible through the back face during the flip.
    setPendingAdvance(idx + 1 >= queue.length ? 'done' : idx + 1);
  };

  // Back during an active session always returns to the deck-picker overview (not Home) —
  // any progress made so far is saved silently (stays on the flashcards screen) so it isn't lost.
  const backToOverview = () => {
    const minutes = timerRef.current?.getActiveMinutes();
    timerRef.current?.stop();
    timerRef.current = null;
    if (reviewed.length > 0) onComplete(reviewed, { toDashboard: false, minutes });
    setPhase('overview');
    setQueue([]); setIdx(0); setFlipped(false); setD1Revealed(false); setReviewed([]);
  };

  return (
    <div className="fc-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={backToOverview}>{t("lesson.backBtn")}</button>
        <div>
          <div className="lesson-title">{t("flashcards.title")}</div>
          <div style={{fontSize:"0.8125rem",color:"#8B7355"}}>{t("flashcards.cardOf", { current: idx + 1, total: queue.length })}</div>
        </div>
      </div>

      <div className="fc-progress-bar">
        <div className="fc-progress-fill" style={{ width: `${(idx / queue.length) * 100}%` }} />
      </div>

      <div className="fc-deck-pill" style={{color: isD2 ? '#059669' : '#7B1E1E', borderColor: isD2 ? '#059669' : '#7B1E1E'}}>
        {isD2 ? `${baseLang} ${dirArrow} ${targetLang}` : `${targetLang} ${dirArrow} ${baseLang}`}
      </div>

      <div className="fc-scene" onClick={() => setFlipped(f => !f)}>
        <div className={`fc-card${flipped ? " flipped" : ""}`}>

          <div className={`fc-face fc-front${isD2 ? " fc-front--d2" : ""}`}>
            <div className="fc-state-pill" style={{background: stateColor + '33', color: stateColor}}>
              {stateLabel}
            </div>
            {isD2 ? (
              <>
                <div className="fc-word" dir="auto">{cardEnglish}</div>
                {topicLabel && <div className="fc-topic-lbl"><bdi>{topicLabel}</bdi></div>}
                <div className="fc-hint">{t("flashcards.tapToReveal")}</div>
              </>
            ) : (
              <>
                <button className="fc-d1-listen-btn" onClick={e => { e.stopPropagation(); speak(card.turkish, card.id, speakLang, 'word'); }}>
                  <VolumeIcon size={28} /> <span>{t("flashcards.d1Listen")}</span>
                </button>
                <div className="fc-d1-reveal-slot">
                  {d1Revealed ? (
                    <div className="fc-word"><bdi>{card.turkish}</bdi></div>
                  ) : (
                    <button className="fc-d1-show-word-btn" onClick={e => { e.stopPropagation(); setD1Revealed(true); }}>
                      {t("flashcards.d1ShowWord")}
                    </button>
                  )}
                </div>
                {topicLabel && <div className="fc-topic-lbl"><bdi>{topicLabel}</bdi></div>}
                <div className="fc-hint">{t("flashcards.tapToReveal")}</div>
              </>
            )}
          </div>

          <div className="fc-face fc-back">
            {isD2 ? (
              <>
                <div className="fc-meaning"><bdi>{card.turkish}</bdi></div>
                <button className="tts-btn-sm" style={{marginBottom:10}} onClick={e=>{e.stopPropagation();speak(card.turkish, card.id, speakLang, 'word');}}>{t("flashcards.hearIt")}</button>
                {cardExample && (
                  <>
                    <div className="fc-example">
                      <em><bdi>{cardExample}</bdi></em>
                    </div>
                    <button className="tts-btn-fc-back" onClick={e=>{e.stopPropagation();speak(cardExample, card.id, speakLang, 'ex1');}}>
                      {t("flashcards.hearSentence")}
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="fc-meaning" dir="auto">{cardEnglish}</div>
                {cardExample && (
                  <>
                    <div className="fc-example">
                      <em dir="auto">{cardExample}</em>
                      {cardExampleTranslation && (
                        <div style={{fontSize:"0.75rem",color:"#8B7355",marginTop:4}} dir="auto">{cardExampleTranslation}</div>
                      )}
                    </div>
                    <button className="tts-btn-fc-back" onClick={e=>{e.stopPropagation();speak(cardExample, card.id, speakLang, 'ex1');}}>
                      {t("flashcards.hearSentence")}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {reviewed.length > 0 && (
        <div className="fc-save-exit-row">
          <button className="fc-save-exit-btn" onClick={() => onComplete(reviewed)}>
            {t("flashcards.saveContinue")}
          </button>
        </div>
      )}

      {flipped && (
        <div className="rating-row">
          {ratingDefs.map(({r,key,cls}) => (
            <button key={r} className={`rating-btn ${cls}`} onClick={() => rate(r)}>
              <div style={{fontWeight:700}}>{t(`flashcards.${key}`)}</div>
              <div style={{fontSize:"0.625rem",opacity:0.7,marginTop:3}}>{fmtIv(previewIv[r-1])}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
